import React, { useState } from 'react';
import { CheckCircle2Icon, DownloadIcon, ExternalLinkIcon, LayersIcon, RotateCcwIcon } from 'lucide-react';
import { loan } from '../../data/loan';
import { providers } from '../../data/providers';
import type { AusResult, HistoryEntry } from '../../types/aus';
import { DetailGroup, DetailRow, SummaryCard } from './SummaryCard';
import { FindingsList } from './FindingsList';
import { ProviderMark } from './ProviderMark';
import { SubmissionHistory } from './SubmissionHistory';
interface ResultsScreenProps {
  results: AusResult[];
  history: HistoryEntry[];
  onRunAgain: () => void;
  onSelectHistory: (entry: HistoryEntry) => void;
  onDone: () => void;
}
export function ResultsScreen({
  results,
  history,
  onRunAgain,
  onSelectHistory,
  onDone
}: ResultsScreenProps) {
  const isDual = results.length > 1;
  const [activeId, setActiveId] = useState(results[0].provider);
  const active = results.find(result => result.provider === activeId) ?? results[0];
  const activeConfig = providers[active.provider];
  const attentionCount = active.findings.filter(finding => finding.severity === 'attention' || finding.severity === 'critical').length;
  return <div className="space-y-4" data-id="element-198" data-mp="2s6f0qmb0xuy0">
      <header className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm" data-id="element-199" data-mp="2s657mpuon7o3">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-200" data-mp="2x6t0lh2h1yky">
          <div className="flex items-start gap-4" data-id="element-201" data-mp="325mc53y77fih">
            {isDual ? <span className="flex items-center" data-id="element-202" data-mp="3747d55l9y37z">
                <ProviderMark provider="fannie" size="md" data-id="element-203" data-mp="372mkxmmfmwda" />
                <span className="-ml-3 rounded-xl ring-2 ring-white" data-id="element-204" data-mp="3bue2j7nm1rra">
                  <ProviderMark provider="freddie" size="md" data-id="element-205" data-mp="3bxa1mzb72mhb" />
                </span>
              </span> : <ProviderMark provider={active.provider} size="md" data-id="element-206" data-mp="3kvua77n1jfrt" />}
            <div data-id="element-207" data-mp="3ktrfwbeuv0ha">
              <div className="flex flex-wrap items-center gap-2" data-id="element-208" data-mp="3pufbzbhxy7id">
                <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight text-graphite-800" data-id="element-209" data-mp="3psku661yuvii">
                  <CheckCircle2Icon aria-hidden="true" className="h-5 w-5 text-emerald-600" data-id="element-210" data-mp="1ktmgfokbqv7l" />
                  Underwriting Complete
                </h2>
                {isDual && <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700" data-id="element-211" data-mp="1zk255dby8lai">
                    <LayersIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-212" data-mp="28hvx6c2oksrv" />
                    Dual AUS Run Report
                  </span>}
              </div>
              <p className="mt-1 text-sm text-slate-500" data-id="element-213" data-mp="2iguz7bsxxq5w">
                {isDual ? 'Fannie Mae Desktop Underwriter + Freddie Mac Loan Product Advisor' : `${activeConfig.name} ${activeConfig.product}`}{' '}
                · Submitted {results[results.length - 1].submittedAt}
              </p>
              <p className="text-sm text-slate-500" data-id="element-214" data-mp="j5albosvcmct">
                {loan.borrower} · Loan #{loan.loanNumber}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-6" data-id="element-215" data-mp="2zq5vvulz738t">
            {results.map(result => <div key={result.provider} className="text-right" data-id="element-216" data-mp="2l1j3ar7c8ezn">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-217" data-mp="2l19dp4q7g9uh">
                  {isDual ? providers[result.provider].shortLabel : 'Recommendation'}
                </p>
                <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3.5 py-1.5 text-sm font-semibold text-white" data-id="element-218" data-mp="2bzjn2145dexr">
                  <CheckCircle2Icon aria-hidden="true" className="h-4 w-4" data-id="element-219" data-mp="2236rsysyet0w" />
                  {result.recommendation}
                </span>
              </div>)}
          </div>
        </div>
      </header>

      {isDual ? <SummaryCard title="Dual Run Comparison" icon={LayersIcon} data-id="element-220" data-mp="btp8gegkx80">
          <div className="grid gap-6 sm:grid-cols-2 sm:gap-8" data-id="element-221" data-mp="5an7em4oxcei">
            {results.map(result => <DetailGroup key={result.provider} label={`${providers[result.provider].name} ${providers[result.provider].shortLabel}`} data-id="element-222" data-mp="21ci6lt7mpept">
                <DetailRow label="Recommendation" value={result.recommendation} data-id="element-223" data-mp="1scbv2cvhqota" />
                <DetailRow label="Eligibility" value={result.eligibility} data-id="element-224" data-mp="1ndqsi6srnt1o" />
                <DetailRow label="Risk Assessment" value={result.riskAssessment} data-id="element-225" data-mp="1nfuw0nmr6q1f" />
                <DetailRow label="Findings" value={`${result.findings.length} messages`} data-id="element-226" data-mp="1ig8mopa1ptxw" />
                <DetailRow label="Submission ID" value={result.submissionId} data-id="element-227" data-mp="1dme3ro29lizv" />
                <DetailRow label={result.caseLabel} value={result.caseNumber} data-id="element-228" data-mp="19nf1kb7x40c4" />
              </DetailGroup>)}
          </div>
        </SummaryCard> : <SummaryCard title="Findings Summary" data-id="element-229" data-mp="znwkc2je6snr">
          <div className="grid gap-6 sm:grid-cols-2 sm:gap-8" data-id="element-230" data-mp="zno6a7bj9haz">
            <DetailGroup label="Assessment" data-id="element-231" data-mp="uol1mnzgtd3j">
              <DetailRow label="Recommendation" value={active.recommendation} data-id="element-232" data-mp="upv0vgi20rbr" />
              <DetailRow label="Eligibility" value={active.eligibility} data-id="element-233" data-mp="kkcxhj6dgv9c" />
              <DetailRow label="Risk Assessment" value={active.riskAssessment} data-id="element-234" data-mp="pjptouj4veol" />
            </DetailGroup>
            <DetailGroup label="Submission" data-id="element-235" data-mp="uj2jno5ax1ve">
              <DetailRow label="Findings" value={`${active.findings.length} messages · ${attentionCount} need attention`} data-id="element-236" data-mp="uh993xrkhxi4" />
              <DetailRow label="Submission ID" value={active.submissionId} data-id="element-237" data-mp="1vzlix4jet5o" />
              <DetailRow label={active.caseLabel} value={active.caseNumber} data-id="element-238" data-mp="6syfnfo7b03v" />
            </DetailGroup>
          </div>
        </SummaryCard>}

      {isDual && <div role="tablist" aria-label="Findings by engine" className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" data-id="element-239" data-mp="gjkomr1ewsdl">
          {results.map(result => {
        const isActive = result.provider === active.provider;
        return <button key={result.provider} type="button" role="tab" aria-selected={isActive} onClick={() => setActiveId(result.provider)} className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 ${isActive ? 'bg-brand-700 text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-240" data-mp="3c7opzwlv93yw">
                {providers[result.provider].name} findings
              </button>;
      })}
        </div>}

      <FindingsList findings={active.findings} data-id="element-241" data-mp="2y8go2vybjhr0" />

      <SubmissionHistory entries={history} onSelect={onSelectHistory} data-id="element-242" data-mp="2y6m2rgj4xnme" />

      <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-lg backdrop-blur" data-id="element-243" data-mp="ig9lojauctb2">
        <button type="button" onClick={onDone} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-244" data-mp="l1qr2k5fhffk">
          Back to Automated Underwriting
        </button>

        <div className="flex flex-wrap items-center gap-2" data-id="element-245" data-mp="g2wbdr2rc6zo">
          <button type="button" onClick={onRunAgain} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-246" data-mp="13rgo7dwnydan">
            <RotateCcwIcon className="h-4 w-4" data-id="element-247" data-mp="yzp51ofu79uj" />
            Run Again
          </button>
          <button type="button" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-248" data-mp="1mq1b0triuwih">
            <DownloadIcon className="h-4 w-4" data-id="element-249" data-mp="xzewvfymrprp" />
            {isDual ? 'Download Dual Report' : 'Download Findings'}
          </button>
          <button type="button" className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${isDual ? 'bg-brand-700 hover:bg-brand-800 focus-visible:ring-brand-700' : `${activeConfig.accentSolid} ${activeConfig.accentSolidHover} ${activeConfig.accentRing}`}`} data-id="element-250" data-mp="syr5uuaej8xh">
            <ExternalLinkIcon className="h-4 w-4" data-id="element-251" data-mp="16s8qyjiqktpm" />
            View Full Findings
          </button>
        </div>
      </div>
    </div>;
}